import { useLocation, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { FunctionComponent, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { UserDetailsContext } from '../context/user-details/user-details';
import { IRoleType, IUserDetails } from '../shared/services/user-details/user-details.model';
import { userHasRequiredRole as hasRequiredRole } from '../shared/utils/role-utils';

const RequireAuth: FunctionComponent<{ children?: PropsWithChildren, allowedRoles?: IRoleType[] }> = ({ children, allowedRoles }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userDetailsContext = useContext(UserDetailsContext);

  function userHasRequiredRole(userDetails: IUserDetails): boolean {
    return hasRequiredRole(userDetails, allowedRoles)
  }

  const [isLoading, setIsLoading] = useState(true);
  const [canAccess, setCanAccess] = useState(false);

  useEffect(() => {

    setIsLoading(true)
    setCanAccess(false)

    async function checkAuth() {
      setIsLoading(true);
      setCanAccess(false);

      let userDetails = userDetailsContext.userDetails;

      if (!userDetails) {
        userDetails = await userDetailsContext.forceUpdateUserDetails();
      }
      if (userDetails) {
        if (userDetails.emailVerificationRequired && location.pathname !== '/email-verification') {
          navigate('/email-verification')
        }
        // Password verified but the second factor is still outstanding: send the
        // user to the MFA challenge rather than into the app.
        if (userDetails.mfaPending && location.pathname !== '/login/verify') {
          navigate('/login/verify')
        }
        if (userDetails.organisation.name === null && userDetails.isUsingSingleSignOn && !userDetails.hasAcceptedTermsAndConditions && location.pathname !== '/complete-sign-up') {
          navigate('/complete-sign-up')
        }
        if (userDetails.organisation.name === null && !userDetails.isUsingSingleSignOn && !userDetails.hasAcceptedTermsAndConditions && location.pathname !== '/complete-registration') {
          navigate('/complete-registration')
        }
        if (!userDetails.organisation.subscriptionActive && location.pathname !== '/dashboard') {
          navigate('/dashboard')
        }
        // if (userDetails.isPartner && !userDetails.isLoggedInAsClient) {
        //   navigate('/partner-dashboard')
        // }
        if (!allowedRoles || allowedRoles.length === 0) {
          setCanAccess(true);
          setIsLoading(false);
        }
        else if (userHasRequiredRole(userDetails)) {
          setCanAccess(true);
          setIsLoading(false);
        } else {
          setIsLoading(false)
        }
      }
      else {
        setIsLoading(false)
      }
    }

    checkAuth()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, allowedRoles])

  // If the user is logged in but lacks required roles, send them to dashboard (not login).
  const hasUser = !!userDetailsContext.userDetails;
  return isLoading
    ? null
    : canAccess
      ? <Outlet />
      : <Navigate to={hasUser ? "/dashboard" : "/login"} state={{ from: location }} replace />;
}

export default RequireAuth;
